import React, { FC } from 'react';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ThemeColorsKeys } from '../../styles/tokens/colors';
import { getBackgroundColor } from '../../utils';
import { Container } from '../container';
import { Spacer } from '../spacer';
import { SectionContextProvider } from '../../context/SectionContext';

// Interface

export interface SectionProps {
  children: any;
  as?: any;
  background?: boolean;
  backgroundColor?: ThemeColorsKeys;
  centered?: boolean;
  narrow?: boolean;
  tight?: boolean;
  id?: string;
}

type StyledSectionProps = Pick<
  SectionProps,
  'backgroundColor' | 'centered' | 'tight'
>;

// Styles

const StyledSection = styled.div<StyledSectionProps>`
  ${({ theme: { space, media }, backgroundColor, centered, tight }) => css`
    padding: ${tight ? space.small : space.medium} 0;
    overflow: hidden;

    // Background and color

    ${backgroundColor && getBackgroundColor(backgroundColor)};

    // Spacing between sibling sections

    &.section--${backgroundColor} + .section--${backgroundColor} {
      padding-top: 0;
    }

    // Centered

    ${centered &&
    css`
      text-align: center;
      margin-right: auto;
      margin-left: auto;
    `};

    @media (min-width: ${media.large}) {
      padding: ${tight ? space.small : space.large} 0;
    }

    @media (min-width: ${media.xLarge}) {
      padding: ${tight ? space.small : space.xLarge} 0;
    }
  `};
`;

// JSX

export const Section: FC<React.PropsWithChildren<SectionProps>> = ({
  as = 'section',
  children,
  background,
  backgroundColor = 'neutralWhite',
  centered,
  narrow,
  tight,
  id,
  ...rest
}) => {
  const backgroundColour = background ? 'neutralLightGrey' : backgroundColor;
  return (
    <StyledSection
      backgroundColor={backgroundColour}
      className={`section--${backgroundColour}`}
      as={as}
      centered={centered}
      tight={tight}
      id={id}
      {...rest}
    >
      <SectionContextProvider value={{ sectionBackground: backgroundColour }}>
        <Container narrow={narrow}>
          <Spacer spacing="medium" responsive>
            {children}
          </Spacer>
        </Container>
      </SectionContextProvider>
    </StyledSection>
  );
};

export default Section;
